import React, { useState, useRef, useEffect, useMemo } from "react";
import styled from "styled-components";
import { isiOSDevice } from "../../../constants/deviceType";
// MAX_MESSAGE_LENGTH import removed - now passed as prop

const InputWrapper = styled.div`
  position: relative;
  flex-grow: 1;
`;

const Input = styled.input`
  margin: 0;
  width: 100%;
  filter: none;
  border-radius: 1rem;
  font-size: var(--fs-body);
  background-color: var(--clr-tert);
  color: var(--clr-sec);
  padding: 0.5rem 4rem 0.5rem 0.75em;
  border: 0px;
  outline: none;
  line-height: 1;
`;

const SuggestionDropdown = styled.div`
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  background-color: var(--clr-prim);
  border-radius: 8px 8px 0 0;
  border: 1px solid var(--clr-tert);
  border-bottom: none;
  max-height: 150px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.15);
`;

const SuggestionItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  cursor: pointer;
  font-size: 14px;
  color: var(--clr-sec);
  background-color: ${props => props.$selected ? 'var(--clr-tert)' : 'transparent'};
  
  &:hover {
    background-color: var(--clr-tert);
  }
`;

const UserColorDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${props => props.$color || 'var(--clr-sec)'};
  flex-shrink: 0;
`;

const SimpleMentionInput = React.forwardRef((props, ref) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef();
  const actualRef = ref || inputRef;

  // Extract users from messages - memoized for performance and real-time updates
  const availableUsers = useMemo(() => {
    if (!props.messages) return [];
    
    const users = new Map();
    props.messages.forEach(msg => {
      if (msg.nickname && msg.userId !== 'self' && msg.nickname.length > 0) {
        users.set(msg.nickname.toLowerCase(), {
          nickname: msg.nickname,
          color: msg.color,
          userId: msg.userId
        });
      }
    });
    
    return Array.from(users.values());
  }, [props.messages]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;
    
    // Check for @ mentions - handle nicknames with spaces
    const beforeCursor = value.slice(0, cursorPos);
    const lastAtIndex = beforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      const query = beforeCursor.slice(lastAtIndex + 1);
      
      // Show suggestions for reasonable length queries
      // Don't restrict spaces since nicknames can have spaces
      if (query.length <= 30) {
        const filtered = availableUsers.filter(user => 
          user.nickname.toLowerCase().includes(query.toLowerCase())
        );
        
        setSuggestions(filtered.slice(0, 8));
        setShowSuggestions(filtered.length > 0);
        setSelectedIndex(0); // Reset selection when suggestions change
      } else {
        setShowSuggestions(false);
      }
    } else {
      setShowSuggestions(false);
    }
    
    // Always call the parent onChange
    props.onChange(value);
  };

  const selectUser = (user) => {
    const input = actualRef.current;
    const value = input.value;
    const cursorPos = input.selectionStart;
    const beforeCursor = value.slice(0, cursorPos);
    const afterCursor = value.slice(cursorPos);
    const lastAtIndex = beforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      const beforeAt = value.slice(0, lastAtIndex);
      const newValue = `${beforeAt}@${user.nickname} ${afterCursor}`;
      props.onChange(newValue);
      
      // Set cursor position after the mention
      setTimeout(() => {
        const newCursorPos = lastAtIndex + user.nickname.length + 2;
        input.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    }
    
    setShowSuggestions(false);
  };

  // Handle keyboard events
  const handleKeyDown = (e) => {
    // Handle suggestions navigation and selection
    if (showSuggestions && suggestions.length > 0) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => 
            prev < suggestions.length - 1 ? prev + 1 : 0
          );
          return;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => 
            prev > 0 ? prev - 1 : suggestions.length - 1
          );
          return;
        case 'Enter':
          e.preventDefault();
          // Select the highlighted suggestion
          if (suggestions[selectedIndex]) {
            selectUser(suggestions[selectedIndex]);
          }
          return;
        case 'Escape':
          e.preventDefault();
          setShowSuggestions(false);
          return;
      }
    }
    
    // Handle Enter key when no suggestions are showing
    if (e.key === 'Enter') {
      e.preventDefault();
      
      // Call the submit function to send message
      if (props.onSubmit) {
        props.onSubmit();
      }
      
      return;
    }
    
    // For all other keys, don't interfere
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setShowSuggestions(false);
    
    if (showSuggestions) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showSuggestions]);

  return (
    <InputWrapper>
      {showSuggestions && suggestions.length > 0 && (
        <SuggestionDropdown>
          {suggestions.map((user, index) => (
            <SuggestionItem
              key={`${user.userId}-${user.nickname}`}
              $selected={index === selectedIndex}
              onClick={() => selectUser(user)}
            >
              <UserColorDot $color={user.color} />
              @{user.nickname}
            </SuggestionItem>
          ))}
        </SuggestionDropdown>
      )}
      <Input
        autoFocus={isiOSDevice() ? false : true}
        autoComplete="off"
        autoCorrect="on"
        autoCapitalize="sentences"
        spellCheck="true"
        inputMode="text"
        enterKeyHint="send"
        ref={actualRef}
        placeholder="What's on your mind?"
        maxLength={props.messageLimit}
        tabIndex="-1"
        disabled={props.disabled}
        value={props.value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={props.onFocus}
        onBlur={props.onBlur}
      />
    </InputWrapper>
  );
});

export default SimpleMentionInput;